import { useState, useEffect } from "react";
import { IconSearch, IconArrowBigRightFilled } from "@tabler/icons-react"
import { TextInput, Button, Paper, Group, Text, Loader, Divider, Badge, Stack } from "@mantine/core";
import { api, type FamilyWithGuests } from "../services/api";

interface SearchProps {
  onFamilySelect: (familyId: number) => void;
  onSearchActive?: (isActive: boolean) => void;
}

export function FamilySearch({ onFamilySelect, onSearchActive }: SearchProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<FamilyWithGuests[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Notify parent when search becomes active (user has typed something)
  useEffect(() => {
    if (onSearchActive) {
      onSearchActive(searchQuery.length > 0);
    }
  }, [searchQuery, onSearchActive]);

  // Auto-search when user types 3 or more characters
  useEffect(() => {
    if (searchQuery.trim().length >= 3) {
      const timeoutId = setTimeout(() => {
        handleSearch();
      }, 300); // Debounce for 300ms

      return () => clearTimeout(timeoutId);
    } else if (searchQuery.trim().length < 2 && hasSearched) {
      // Clear results if search query is too short
      setSearchResults([]);
      setHasSearched(false);
    }
  }, [searchQuery]);

  const handleSearch = async () => {
    if (searchQuery.trim().length < 2) {
      return;
    }

    setIsSearching(true);
    setHasSearched(true);

    try {
      const families = await api.searchFamilies(searchQuery);
      setSearchResults(families || []);
    } catch (error) {
      console.error("Search failed:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div>
      <Paper p="xl" shadow="sm" style={{ marginBottom: "2rem" }}>
        <Text size="xl" fw={700} style={{ marginBottom: "1rem" }}>
          Trouvez votre invitation
        </Text>
        <Text size="sm" c="dimmed" style={{ marginBottom: "1.5rem" }}>
          Recherchez par nom de clan, prénom ou nom
        </Text>
        
        <Group gap="sm">
          <TextInput
            placeholder="Clan, prénom, nom..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            style={{ flex: 1 }}
            size="md"
          />
          <Button 
            onClick={handleSearch} 
            disabled={searchQuery.trim().length < 2 || isSearching}
            size="md"
          >
            {isSearching ? <Loader size="sm" color="white" /> : <IconSearch />}
          </Button>
        </Group>

        {hasSearched && (
          <div>
            <Divider my="lg" />
            {searchResults.length > 0 ? (
              <div>
                <Text size="sm" c="dimmed" style={{ marginBottom: "1rem" }}>
                  {searchResults.length} {searchResults.length === 1 ? "clan trouvé" : "clans trouvés"}:
                </Text>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  {searchResults.map((family) => (
                    <Paper
                      key={family.id}
                      p="md"
                      shadow="xs"
                      style={{ cursor: "pointer", transition: "all 0.2s" }}
                      onClick={() => onFamilySelect(family.id)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateX(4px)";
                        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateX(0)";
                        e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
                      }}
                    >
                      <Text fw={600} size="lg" style={{ marginBottom: "0.5rem" }}>
                        Clan <em>{family.family_name}</em>
                      </Text>
                      <Group justify="space-between" align="flex-end">
                      <Stack gap="xs">
                        {family.guests.map((guest) => (
                        <Group key={guest.id} gap="xs">
                          <Text size="sm" c="dimmed">
                          {guest.first_name} {guest.last_name}
                          </Text>
                          {guest.is_child && (
                          <Badge color="blue" size="xs" variant="light">
                            👶
                          </Badge>
                          )}
                        </Group>
                        ))}
                      </Stack>
                      <IconArrowBigRightFilled size={24} color="lightblue" />
                      </Group>
                    </Paper>
                  ))}
                </div>
              </div>
            ) : (
              !isSearching && (
                <Paper p="xl" shadow="xs" style={{ textAlign: "center" }}>
                  <Text c="dimmed">Pas de résultat pour "{searchQuery}"</Text>
                  <Text size="sm" c="dimmed" style={{ marginTop: "0.5rem" }}>
                    Veuillez essayer un terme de recherche différent
                  </Text>
                </Paper>
              )
            )}
          </div>
        )}
      </Paper>
    </div>
  );
}
